import { useEffect, useState } from 'react';
import { DebugKv, DebugSection } from '../DebugSection';

export interface RoutingSectionProps { searchTerm?: string }

interface NavEntry {
  pathname: string;
  search: string;
  hash: string;
  at: string;
}

const HISTORY_KEY = 'pcb.debug.nav.history';
const MAX_HISTORY = 20;

function readHistory(): NavEntry[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.sessionStorage.getItem(HISTORY_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (Array.isArray(parsed)) return parsed.slice(-MAX_HISTORY) as NavEntry[];
  } catch { /* ignore */ }
  return [];
}

function pushHistory(): void {
  if (typeof window === 'undefined') return;
  const buf = readHistory();
  const last = buf[buf.length - 1];
  const cur: NavEntry = {
    pathname: window.location.pathname,
    search: window.location.search,
    hash: window.location.hash,
    at: new Date().toISOString(),
  };
  if (last && last.pathname === cur.pathname && last.search === cur.search && last.hash === cur.hash) return;
  buf.push(cur);
  while (buf.length > MAX_HISTORY) buf.shift();
  try { window.sessionStorage.setItem(HISTORY_KEY, JSON.stringify(buf)); } catch { /* quota */ }
}

export function RoutingSection({ searchTerm }: RoutingSectionProps): JSX.Element {
  const [history, setHistory] = useState<NavEntry[]>(() => readHistory());

  useEffect(() => {
    if (typeof window === 'undefined') return;
    pushHistory();
    setHistory(readHistory());
    const tick = (): void => {
      pushHistory();
      setHistory(readHistory());
    };
    window.addEventListener('popstate', tick);
    window.addEventListener('hashchange', tick);
    // Hook history.pushState/replaceState — best-effort patch.
    const orig = window.history.pushState;
    const origR = window.history.replaceState;
    window.history.pushState = function patched(...args: Parameters<typeof orig>) {
      const r = orig.apply(this, args);
      setTimeout(tick, 0);
      return r;
    };
    window.history.replaceState = function patched(...args: Parameters<typeof origR>) {
      const r = origR.apply(this, args);
      setTimeout(tick, 0);
      return r;
    };
    return () => {
      window.removeEventListener('popstate', tick);
      window.removeEventListener('hashchange', tick);
      window.history.pushState = orig;
      window.history.replaceState = origR;
    };
  }, []);

  const cur = typeof window !== 'undefined' ? {
    pathname: window.location.pathname,
    search: window.location.search,
    hash: window.location.hash,
    href: window.location.href,
    historyLength: window.history.length,
  } : {};

  const rows = Object.entries(cur).map(([k, v]) => ({ k, v }));

  return (
    <DebugSection
      id="routing"
      title="7. Routing + navigation"
      count={history.length}
      searchTerm={searchTerm}
    >
      <div className="pcb-debug-subhead">current</div>
      <DebugKv rows={rows} searchTerm={searchTerm} />
      <div className="pcb-debug-subhead">history (last {history.length})</div>
      <ol className="pcb-debug-nav-list">
        {history.slice().reverse().map((h, i) => (
          <li key={i} className="pcb-debug-nav-item">
            <code className="pcb-debug-mono">{h.at.slice(11, 19)}</code>
            <span className="pcb-debug-nav-path">{h.pathname}{h.search}{h.hash}</span>
          </li>
        ))}
      </ol>
    </DebugSection>
  );
}
